import type { AxiosResponse } from 'axios'
import axios from 'axios'

import type {
    Machine,
    MachineColourWasteConfiguration,
    MachineColourWasteConfigurationPayload,
    MachineCostCenterNumberResponse,
    MachineDictionaryResponse,
    MachineFoldingWasteConfiguration,
    MachineFoldingWasteConfigurationPayload,
    MachineListResponse,
    MachineProductionDepartmentResponse,
    MachineWasteAmountPayload,
    MachineWastePercentConfiguration,
    MachineWastePercentConfigurationPayload
} from '@/api/schemas/configurations/machines'
import { API_ENDPOINTS, MACHINES } from '@/constants'
import { buildEndpoint } from '@/utils/apiEndpointUtils'

type QueryParams = Record<string, unknown>
type MachinePayload = Record<string, unknown>
type MachineProcessCostPayload = Record<string, unknown>

class MachinesQueries {
    // List & details
    fetchMachines(params?: QueryParams): Promise<AxiosResponse<MachineListResponse>> {
        return axios.get<MachineListResponse>(API_ENDPOINTS.MACHINES.LIST, { params })
    }

    fetchMachinesRaw(params?: QueryParams): Promise<AxiosResponse<MachineListResponse>> {
        return axios.get<MachineListResponse>(API_ENDPOINTS.MACHINES.RAW, { params })
    }

    fetchCalculationMachines(params?: QueryParams): Promise<AxiosResponse<MachineListResponse>> {
        return axios.get<MachineListResponse>(API_ENDPOINTS.MACHINES.CALCULATION_MACHINES, { params })
    }

    fetchMachine(id: number): Promise<AxiosResponse<Machine>> {
        return axios.get<Machine>(`${API_ENDPOINTS.MACHINES.LIST}/${id}`)
    }

    createMachine(payload: MachinePayload): Promise<AxiosResponse<Machine>> {
        return axios.post<Machine>(API_ENDPOINTS.MACHINES.LIST, payload)
    }

    updateMachine(id: number, payload: MachinePayload): Promise<AxiosResponse<Machine>> {
        return axios.put<Machine>(`${API_ENDPOINTS.MACHINES.LIST}/${id}`, payload)
    }

    patchMachine(id: number, payload: MachineWasteAmountPayload): Promise<AxiosResponse<Machine>> {
        return axios.patch<Machine>(`${API_ENDPOINTS.MACHINES.LIST}/${id}`, payload)
    }

    fetchMachineFoldingWasteConfigurations(machineId: number): Promise<AxiosResponse<MachineFoldingWasteConfiguration[]>> {
        return axios.get<MachineFoldingWasteConfiguration[]>(API_ENDPOINTS.MACHINES.MACHINE_FOLDING_WASTE_CONFIGURATIONS, {
            params: { machine_id: machineId }
        })
    }

    updateMachineFoldingWasteConfigurations(
        payload: MachineFoldingWasteConfigurationPayload
    ): Promise<AxiosResponse<MachineFoldingWasteConfigurationPayload>> {
        return axios.put<MachineFoldingWasteConfigurationPayload>(
            API_ENDPOINTS.MACHINES.MACHINE_FOLDING_WASTE_CONFIGURATIONS,
            payload
        )
    }

    fetchMachineColourWasteConfigurations(machineId: number): Promise<AxiosResponse<MachineColourWasteConfiguration[]>> {
        return axios.get<MachineColourWasteConfiguration[]>(API_ENDPOINTS.MACHINES.MACHINE_COLOUR_WASTE_CONFIGURATIONS, {
            params: { machine_id: machineId }
        })
    }

    updateMachineColourWasteConfigurations(
        payload: MachineColourWasteConfigurationPayload
    ): Promise<AxiosResponse<MachineColourWasteConfigurationPayload>> {
        return axios.put<MachineColourWasteConfigurationPayload>(
            API_ENDPOINTS.MACHINES.MACHINE_COLOUR_WASTE_CONFIGURATIONS,
            payload
        )
    }

    fetchColourTypes(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(API_ENDPOINTS.MACHINES.COLOUR_TYPES)
    }

    fetchMachineWastePercentConfigurations(machineId: number): Promise<AxiosResponse<MachineWastePercentConfiguration[]>> {
        return axios.get<MachineWastePercentConfiguration[]>(
            buildEndpoint(API_ENDPOINTS.MACHINES.MACHINE_WASTE_PERCENT_CONFIGURATIONS, { machineId })
        )
    }

    updateMachineWastePercentConfigurations(
        machineId: number,
        payload: MachineWastePercentConfigurationPayload
    ): Promise<AxiosResponse<MachineWastePercentConfiguration[]>> {
        return axios.put<MachineWastePercentConfiguration[]>(
            buildEndpoint(API_ENDPOINTS.MACHINES.MACHINE_WASTE_PERCENT_CONFIGURATIONS, { machineId }),
            payload
        )
    }

    // Dictionaries
    fetchMachinePrintTypes(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(`${API_ENDPOINTS.MACHINES.DICTS}/machine-print-types`)
    }

    fetchMachineSheetFormats(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(`${API_ENDPOINTS.MACHINES.DICTS}/machine-sheet-formats`)
    }

    fetchMachinePlateTypes(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(`${API_ENDPOINTS.MACHINES.DICTS}/machine-plate-types`)
    }

    fetchMachineDurGroups(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(`${API_ENDPOINTS.MACHINES.DICTS}/machine-dur-groups`)
    }

    fetchCostCenterNumbers(): Promise<AxiosResponse<MachineCostCenterNumberResponse>> {
        return axios.get<MachineCostCenterNumberResponse>(API_ENDPOINTS.MACHINES.COST_CENTER_NUMBERS, {
            params: { limit: MACHINES.COST_CENTER_NUMBERS_LIMIT }
        })
    }

    // Production processes
    fetchProductionProcesses(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(API_ENDPOINTS.MACHINES.PRODUCTION_PROCESSES)
    }

    fetchProductionProcessesRaw(): Promise<AxiosResponse<MachineDictionaryResponse>> {
        return axios.get<MachineDictionaryResponse>(API_ENDPOINTS.MACHINES.PRODUCTION_PROCESSES_RAW)
    }

    // Machine process costs
    fetchMachineProcessCost(machineId: number): Promise<AxiosResponse<Machine>> {
        return this.fetchMachine(machineId)
    }

    updateMachineProcessCost(machineProcessCostId: number, data: MachineProcessCostPayload): Promise<AxiosResponse<Machine>> {
        return axios.put<Machine>(`${API_ENDPOINTS.MACHINES.MACHINE_PROCESS_COSTS}/${machineProcessCostId}`, data)
    }

    // Production departments
    fetchProductionDepartments(
        queryOrParams: string | QueryParams = ''
    ): Promise<AxiosResponse<MachineProductionDepartmentResponse>> {
        if (typeof queryOrParams === 'string') {
            return axios.get<MachineProductionDepartmentResponse>(
                `${API_ENDPOINTS.MACHINES.PRODUCTION_DEPARTMENTS}${queryOrParams}`
            )
        }
        return axios.get<MachineProductionDepartmentResponse>(API_ENDPOINTS.MACHINES.PRODUCTION_DEPARTMENTS, {
            params: queryOrParams
        })
    }
}

export default new MachinesQueries()
